import React, { useCallback, useState } from 'react';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../../offline/useRevalidateOnSync';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Styles
import { Container } from './styles';
import { ContainerButton } from '../../components/MainStyledComponents';

// Types
import {
  tableIds,
  takeBase,
} from '../../../../../components/display/tables/types';
import { firstPageTake } from '../../../../../utils/cursor-take.helper';

// Components
import { ValidationsTable } from '../../../../../components/display/tables/ValidationsTable';
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';
import { dataTypes } from '../../../../../components/display/tables/ValidationsTable/types';

// Constants
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../../../components/forms/ActionForms/constants';

// Utils
import { compressSessionStorage } from '../../../../../utils/sessionStorage';

// GQL
import { client } from '../../../../../gql/client';
import {
  GET_ACTION_BY_ID,
  FETCH_ACTIONS,
} from '../../../../../gql/queries/actions/actions';
import { GET_ORDER_BY_ID } from '../../../../../gql/queries/orders/orders';

// Redux
import { actionTabsUnlockAll } from '../../../../../state/actions/ui/actionTabs';
import { getCursor } from '../../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { externalCompaniesNaming } from '../../../../../components/display/tables/formTables/ExternalCompaniesSelectionTable/constants';
import { store } from '../../../../../state/configureStore';

// Hooks
import { usePollingRevalidation } from '../../../../../hooks/usePollingRevalidation';
import { withNavigationFeedback } from '../../../../../utils/navigationFeedback';

// Página global de Actuaciones, parametrizable para reutilizarse como pestaña
// de un LISTADO de módulo. Todos los props son opcionales y sus defaults
// reproducen el comportamiento global byte-idéntico (`origin`/`returnTo` ya
// estaban hardcodeados aquí antes de parametrizarse).
//
// `containerTypes` filtra por TIPO de origen: "todas las actuaciones de
// recorridos de vigilancia" (listado RV) o "de campañas" (listado Campañas).
//
// `containerId` (filtro por un container CONCRETO) se quedó SIN consumidores
// al retirarse las pestañas del detalle de campaña: hoy los llamadores — el
// listado de Campañas y el del módulo RV — usan `containerTypes`. Se conserva
// la parametrización, que es lo que hace reutilizable esta página.
export type ContextualListProps = {
  containerId?: string;
  containerTypes?: string[];
  tableId?: string;
  origin?: string;
  returnTo?: string;
};

export const ActionsPage = ({
  containerId,
  containerTypes,
  tableId = tableIds.GENERAL_ACTIONS,
  origin = 'actions-tab',
  returnTo = '/work-orders/actions',
}: ContextualListProps = {}) => {
  const [rowState, setRowState] = useState('');
  const loaderData = useAsyncValue() as dataTypes;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  useRevalidateOnSync();
  const { t } = useTranslation('pages');

  // Abre la actuación `id`. Lo usan el botón (con la fila seleccionada) y el
  // doble toque de la tabla, para que los dos graben el mismo origen y vuelvan
  // al mismo sitio (spec 2026-10-06-return-to-origin § 4 E1).
  const openAction = useCallback((id: string) =>
    withNavigationFeedback(async () => {
    const {
      data: { action },
    } = await client.query({
      query: GET_ACTION_BY_ID,
      variables: { id },
    });

    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: action.orderIDs[0] },
    });

    // Clear previous session storage data
    Object.values({
      ...formActionNaming,
      ...formActionValidationNaming,
      ...externalCompaniesNaming,
    }).forEach((key) => {
      key !== formActionNaming.NEW_ACTION_DATA &&
        sessionStorage.removeItem(key);
    });

    // Store action data in sessionStorage
    sessionStorage.setItem(
      formActionNaming.NEW_VALIDATION_ACTION_DATA,
      compressSessionStorage(
        JSON.stringify({
          id,
          orderId: order.id,
          roid: order.roid,
          isActionEdited: 'false',
          origin,
          returnTo,
        })
      )
    );

    // FLUJO VALIDACIÓN: Desbloquear TODAS las tabs
    dispatch(actionTabsUnlockAll());
    // Navigate to actions-validation edit page
    navigate(`/work-orders/actions-validation/summary/${id}`);
  }), [dispatch, navigate, origin, returnTo]);

  const handleClick = useCallback(() => openAction(rowState), [openAction, rowState]);

  const onReFetchData = useCallback(async () => {
    // Always read from store.getState() — avoids stale closure
    const currentCursor = getCursor(tableId)(store.getState());
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    // Fase 4 nav Trabajo/Revisión — `containerTypes` prop (estático, RV) gana
    // sobre el cursor; en la tabla GLOBAL el prop está vacío y el valor sale
    // del filtro "Origen" user-driven persistido en el cursor.
    const effectiveContainerTypes = containerTypes?.length
      ? containerTypes
      : currentCursor.containerTypes;
    const { data } = await client.query({
      query: FETCH_ACTIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor?.sortField,
          orderBy: currentCursor?.orderBy ?? 'desc',
          concessionId: currentCursor.concessionId,
          toDate: currentCursor.toDate,
          fromDate: currentCursor.fromDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          predefinedOrderId: currentCursor.predefinedOrderId,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          operatorIds: currentCursor.operatorIds,
          teamIds: currentCursor.teamIds,
          externalCompanyId: currentCursor.externalCompanyId,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
          track: currentCursor?.track,
          activityId: currentCursor?.activityId,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor?.onlyFlagged,
          // GOTCHA (review T1): omitir en vez de null/undefined explícito —
          // `containerId: null` SÍ entra en la keyArgs de Apollo y partiría
          // la caché histórica sin containerId (ver policies.ts). Misma
          // regla para `containerTypes` (Fase 3 nav rework).
          ...(containerId && { containerId }),
          ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
        },
      },
      fetchPolicy: 'network-only',
    });

    const setCursorData = {
      take: effectiveTake,
      cursor: '',
      // #74: sortField/orderBy se OMITEN — el merge del reducer preserva los
      // recién seteados por SortableHeader; re-despacharlos pisaría el asc.
      concessionId: currentCursor?.concessionId,
      toDate: currentCursor?.toDate,
      fromDate: currentCursor?.fromDate,
      indexType: currentCursor?.indexType,
      indexExpedition: currentCursor?.indexExpedition,
      classification: currentCursor?.classification,
      predefinedOrderId: currentCursor?.predefinedOrderId,
      capitolId: currentCursor?.capitolId,
      subCapitolId: currentCursor?.subCapitolId,
      states: currentCursor?.states,
      userIds: currentCursor?.userIds,
      operatorIds: currentCursor?.operatorIds,
      teamIds: currentCursor?.teamIds,
      externalCompanyId: currentCursor?.externalCompanyId,
      pkInitKm: currentCursor?.pkInitKm,
      pkInitMeter: currentCursor?.pkInitMeter,
      pkEndKm: currentCursor?.pkEndKm,
      pkEndMeter: currentCursor?.pkEndMeter,
      track: currentCursor?.track,
      activityId: currentCursor?.activityId,
      onlyFlagged: currentCursor?.onlyFlagged,
      // Redux (no Apollo): full-replace de la slice del cursor — se escribe
      // el containerId/containerTypes del prop para no perderlos en el
      // replace; undefined en el path global.
      containerId,
      containerTypes: effectiveContainerTypes,
    };

    dispatch(setCursor(tableId, setCursorData));
    revalidator.revalidate();
  }, [tableId, containerId, containerTypes]);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, [revalidator]);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableId)(store.getState());
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    const effectiveContainerTypes = containerTypes?.length
      ? containerTypes
      : currentCursor.containerTypes;
    await client.query({
      query: FETCH_ACTIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor?.sortField,
          orderBy: currentCursor?.orderBy ?? 'desc',
          concessionId: currentCursor.concessionId,
          toDate: currentCursor.toDate,
          fromDate: currentCursor.fromDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          predefinedOrderId: currentCursor.predefinedOrderId,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          operatorIds: currentCursor.operatorIds,
          teamIds: currentCursor.teamIds,
          externalCompanyId: currentCursor.externalCompanyId,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
          track: currentCursor?.track,
          activityId: currentCursor?.activityId,
          // Sin esto la lista filtrada dejaría de refrescarse en silencio.
          onlyFlagged: currentCursor?.onlyFlagged,
          ...(containerId && { containerId }),
          ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, [tableId, containerId, containerTypes]);
  usePollingRevalidation(onPollingRefresh);

  return (
    <Container>
      <ValidationsTable
        titleText={t('workOrders.filterActions')}
        setRowState={setRowState}
        data={loaderData}
        tableId={tableId}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        onRowOpen={openAction}
        // Banderitas (spec 2026-10-22 § 8): columna y filtro «Solo marcadas»
        // (Actuaciones y las pestañas de actuaciones de los listados de
        // Recorridos y de Campañas, que montan esta misma página).
        showFlagColumn
        showOnlyFlaggedFilter
      />
      <ContainerButton>
        <MainButton
          text={t('workOrders.viewAction')}
          disabled={rowState.length === 0}
          onClick={handleClick}
        />
      </ContainerButton>
    </Container>
  );
};
